import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ForcePasswordChangeModal() {
  const { t } = useTranslation();
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  const rules = [
    { ok: next.length >= 8,                       label: t("forcePasswordChange.rule.minLen", { defaultValue: "Mínimo 8 caracteres" }) },
    { ok: /[A-Z]/.test(next),                     label: t("forcePasswordChange.rule.upper",  { defaultValue: "Al menos una mayúscula" }) },
    { ok: /[0-9]/.test(next),                     label: t("forcePasswordChange.rule.digit",  { defaultValue: "Al menos un número" }) },
    { ok: next === confirm && confirm.length > 0, label: t("forcePasswordChange.rule.match",  { defaultValue: "Las contraseñas coinciden" }) },
  ];
  const valid = rules.every(r => r.ok);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setError("");
    setSaving(true);
    try {
      await changePassword(current, next);
      toast.success(t("forcePasswordChange.success"));
    } catch (err) {
      setError(err.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-[#007934] px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Lock size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-['Outfit']">{t("forcePasswordChange.title")}</h2>
              <p className="text-white/70 text-xs mt-0.5">
                {t("forcePasswordChange.helloUser", { name: user?.name?.split(" ")[0] || "" })}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Current password */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">{t("forcePasswordChange.currentLabel")}</label>
            <div className="relative">
              <input
                type={showCur ? "text" : "password"}
                value={current}
                onChange={e => setCurrent(e.target.value)}
                placeholder={t("forcePasswordChange.currentPlaceholder")}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#007934] transition-colors"
                data-testid="current-password-input"
                required
              />
              <button type="button" onClick={() => setShowCur(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showCur ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">{t("forcePasswordChange.newLabel")}</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={next}
                onChange={e => setNext(e.target.value)}
                placeholder={t("forcePasswordChange.newPlaceholder")}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#007934] transition-colors"
                data-testid="new-password-input"
                required
              />
              <button type="button" onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">{t("forcePasswordChange.repeatLabel")}</label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder={t("forcePasswordChange.repeatPlaceholder")}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#007934] transition-colors"
              data-testid="confirm-password-input"
              required
            />
          </div>

          {/* Rules checklist */}
          {next.length > 0 && (
            <div className="space-y-1 bg-gray-50 rounded-xl px-3 py-2.5">
              {rules.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle size={12} className={r.ok ? "text-[#007934]" : "text-gray-300"} />
                  <span className={`text-xs ${r.ok ? "text-[#007934] font-medium" : "text-gray-400"}`}>{r.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-3 py-2.5 text-sm">
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={logout}
              className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors"
              data-testid="logout-instead-btn"
            >
              {t("forcePasswordChange.logout")}
            </button>
            <button
              type="submit"
              disabled={!valid || saving || !current}
              className="flex-1 bg-[#007934] text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              data-testid="save-new-password-btn"
            >
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> {t("forcePasswordChange.submitting")}</>
                : <><Lock size={14} /> {t("forcePasswordChange.submit")}</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
